import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ShoppingBag, ArrowLeft, Trash2, Plus, Minus, MapPin,
  Truck, CheckCircle2, XCircle, Loader2, Info,
} from "lucide-react";
import { useCart } from "./cartContext";
import { useTenant } from "./tenantContext";
import ShopFooter from "./ShopFooter";

const IMAGE_DISCLAIMER =
  "Image for reference only. Actual product may differ.";

export default function ShopCheckout() {
  const nav = useNavigate();
  const { list, subtotal, add, remove, clear } = useCart();
  const { ownerId } = useTenant();
  const [me, setMe] = useState(null);
  const [quote, setQuote] = useState(null); // { serviced, charge, area, reason }
  const [checking, setChecking] = useState(true);
  const [notes, setNotes] = useState("");
  const [placing, setPlacing] = useState(false);
  const [payMethod, setPayMethod] = useState("prepaid"); // "prepaid" | "cod"
  // Which gateway takes online payments: { phonepe, razorpay } for this shop.
  const [gateways, setGateways] = useState({ phonepe: false, razorpay: true });
  const [gateway, setGateway] = useState("razorpay"); // "phonepe" | "razorpay"

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/store/me");
      setMe(data);
    } catch {
      // Not logged in — checkout requires auth. Redirect only when the
      // customer actually reaches the cart page.
      nav("/shop/login?next=/shop/checkout");
    }
  }, [nav]);

  useEffect(() => { loadMe(); }, [loadMe]);

  useEffect(() => {
    if (!me) return;
    api.get("/store/payments/options")
      .then(({ data }) => {
        setGateways(data);
        setGateway(data.phonepe ? "phonepe" : "razorpay");
      })
      .catch(() => {});
  }, [me]);

  // Load Razorpay Checkout script once on mount so it's ready when user
  // clicks Place Order.
  useEffect(() => {
    if (document.getElementById("rzp-script")) return;
    const s = document.createElement("script");
    s.id = "rzp-script";
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  // Re-run the shipping quote whenever the customer's PIN changes.
  useEffect(() => {
    if (!me?.pin_code) return;
    setChecking(true);
    const params = { pin: me.pin_code };
    if (ownerId) params.owner_id = ownerId;
    api.get(`/store/shipping-preview`, { params })
      .then((r) => setQuote(r.data))
      .catch(() => setQuote({ serviced: false, reason: "Could not verify delivery." }))
      .finally(() => setChecking(false));
  }, [me?.pin_code, ownerId]);

  // If COD is blocked for this PIN, snap the selection back to prepaid.
  useEffect(() => {
    if (quote?.serviced && quote.cod_allowed === false && payMethod === "cod") {
      setPayMethod("prepaid");
    }
  }, [quote, payMethod]);

  const codBlocked = quote?.serviced && quote?.cod_allowed === false;

  const total = useMemo(
    () => subtotal + (quote?.serviced ? Number(quote.charge || 0) : 0),
    [subtotal, quote]
  );

  const canPlace = list.length > 0 && quote?.serviced && !placing;

  const place = async () => {
    if (!canPlace) return;
    setPlacing(true);
    try {
      const payload = { items: list.map((i) => ({ item_id: i.id, quantity: i.quantity })), notes, payment_method: payMethod };
      const { data: order } = await api.post("/store/orders", payload);

      // Send the customer to their public tracking page carrying the fresh
      // `?t=<token>` and `?fresh=1` flag. That page:
      //   • carries the signed token IN THE URL BAR so bookmarking works
      //   • shows a big "Save this link" banner when `fresh=1` is present
      //   • never requires a login — even if the customer logs out later
      //     they can revisit this exact URL to see the delivery PIN.
      const trackPath = order.tracking_token
        ? `/shop/track/${order.id}?t=${encodeURIComponent(order.tracking_token)}&fresh=1`
        : `/shop/orders`;

      if (payMethod === "cod") {
        toast.success("Order placed! Save the link on the next screen.");
        clear();
        nav(trackPath);
        return;
      }

      if (gateway === "phonepe") {
        // Pay on PhonePe's page; PhonePe sends the customer back to the
        // tracking page, which asks the server whether it was paid.
        const { data: pp } = await api.post("/store/payments/phonepe/create", {
          order_id: order.id,
          t: order.tracking_token,
        });
        clear();
        window.location.href = pp.redirect_url;
        return;
      }

      // Prepaid: create Razorpay order, open Checkout modal.
      const { data: pay } = await api.post("/store/payments/create", { order_id: order.id });

      const openRazorpay = () => new Promise((resolve, reject) => {
        if (!window.Razorpay) return reject(new Error("Payment SDK not loaded"));
        const rzp = new window.Razorpay({
          key: pay.key_id,
          amount: pay.amount,
          currency: pay.currency,
          order_id: pay.razorpay_order_id,
          name: "DialFlow Shop",
          description: `Order ${order.id.slice(0, 8)}`,
          prefill: { name: me.name, contact: me.mobile.replace("+", "") },
          theme: { color: "var(--shop-primary,#0055FF)" },
          handler: async (r) => {
            try {
              await api.post("/store/payments/verify", {
                order_id: order.id,
                razorpay_order_id: r.razorpay_order_id,
                razorpay_payment_id: r.razorpay_payment_id,
                razorpay_signature: r.razorpay_signature,
              });
              resolve(r);
            } catch (e) { reject(e); }
          },
          modal: { ondismiss: () => reject(new Error("Payment cancelled")) },
        });
        rzp.open();
      });

      await openRazorpay();
      toast.success("Payment successful! Save the link on the next screen.");
      clear();
      nav(trackPath);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Could not place order");
    } finally {
      setPlacing(false);
    }
  };

  if (!me) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <Link to="/shop" className="text-slate-500 hover:text-slate-900 flex items-center gap-1 text-sm">
          <ArrowLeft className="w-4 h-4" /> Continue shopping
        </Link>
        <div className="flex items-center gap-2">
          <ShoppingBag className="w-5 h-5 text-[var(--shop-primary,#0055FF)]" />
          <span className="font-heading font-bold">Checkout</span>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-4 md:p-6 grid gap-4 md:grid-cols-3">
        {/* Left: cart items */}
        <div className="md:col-span-2 space-y-3">
          {list.length === 0 ? (
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-8 text-center text-slate-500 text-sm">
                Your cart is empty. <Link to="/shop" className="text-[var(--shop-primary,#0055FF)] hover:underline">Browse products</Link>
              </CardContent>
            </Card>
          ) : (
            list.map((it) => (
              <Card key={it.id} className="border-slate-200 shadow-none" data-testid={`cart-line-${it.id}`}>
                <CardContent className="p-3 flex gap-3 items-center">
                  <div className="w-16 h-16 bg-slate-100 rounded overflow-hidden shrink-0">
                    {it.image ? <img src={it.image} alt={it.name} className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{it.name}</div>
                    <div className="mono text-xs text-slate-500 mt-0.5">₹{Number(it.price).toLocaleString("en-IN")} each</div>
                    <div className="flex items-center gap-1 mt-1.5">
                      <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => add({ ...it, images: it.image ? [it.image] : [] }, -1)} data-testid={`cart-dec-${it.id}`}>
                        <Minus className="w-3 h-3" />
                      </Button>
                      <div className="mono text-sm w-8 text-center">{it.quantity}</div>
                      <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => add({ ...it, images: it.image ? [it.image] : [] }, 1)} data-testid={`cart-inc-${it.id}`}>
                        <Plus className="w-3 h-3" />
                      </Button>
                      <button
                        onClick={() => remove(it.id)}
                        className="ml-2 text-slate-400 hover:text-red-600 p-1"
                        title="Remove"
                        data-testid={`cart-remove-${it.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="mono font-semibold text-right shrink-0">
                    ₹{(Number(it.price) * it.quantity).toLocaleString("en-IN")}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
          {list.length > 0 && (
            <p className="text-amber-800 text-xs flex items-center gap-1 pt-1">
              <Info className="w-3 h-3" /> {IMAGE_DISCLAIMER}
            </p>
          )}
        </div>

        {/* Right: address + shipping + total */}
        <div className="space-y-3">
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-4">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Deliver to</div>
              <div className="font-medium text-sm">{me.name}</div>
              <div className="text-xs text-slate-600 mt-0.5">{me.mobile}</div>
              <div className="text-xs text-slate-600 mt-0.5 whitespace-pre-wrap">{me.address}</div>
              <div className="flex items-center gap-1 mono text-xs mt-1 text-slate-600">
                <MapPin className="w-3 h-3" /> PIN {me.pin_code}
              </div>
              <Link
                to="/shop/account"
                className="mt-2 text-xs text-[var(--shop-primary,#0055FF)] hover:underline inline-block"
                data-testid="cart-edit-address"
              >
                Edit address
              </Link>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-4">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1">
                <Truck className="w-3.5 h-3.5" /> Shipping
              </div>
              {checking ? (
                <div className="text-xs text-slate-500 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Checking delivery…</div>
              ) : quote?.serviced ? (
                <div className="text-xs text-emerald-700 flex items-center gap-1" data-testid="cart-ship-serviced">
                  <CheckCircle2 className="w-4 h-4" />
                  {quote.area || "Serviced"} · ₹{Number(quote.charge).toLocaleString("en-IN")}
                </div>
              ) : (
                <div className="text-xs text-red-600 flex items-start gap-1" data-testid="cart-ship-blocked">
                  <XCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{quote?.reason || "Delivery not available to your PIN."}</span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-4 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Subtotal</span>
                <span className="mono">₹{subtotal.toLocaleString("en-IN")}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Shipping</span>
                <span className="mono">
                  {quote?.serviced ? `₹${Number(quote.charge).toLocaleString("en-IN")}` : "—"}
                </span>
              </div>
              <div className="border-t border-slate-100 pt-2 flex justify-between font-semibold">
                <span>Total</span>
                <span className="mono" data-testid="cart-total">₹{total.toLocaleString("en-IN")}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-4">
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                Payment method
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 border border-slate-200 rounded-md p-2 cursor-pointer hover:bg-slate-50">
                  <input type="radio" name="pay" checked={payMethod === "prepaid"} onChange={() => setPayMethod("prepaid")} data-testid="pay-prepaid" />
                  <div>
                    <div className="text-sm font-medium">Pay online</div>
                    <div className="mono text-[10px] text-slate-500">
                      UPI · Card · Netbanking · {gateway === "phonepe" ? "PhonePe" : "Razorpay"}
                    </div>
                  </div>
                </label>
                {payMethod === "prepaid" && gateways.phonepe && gateways.razorpay && (
                  <div className="flex gap-2 pl-6" data-testid="pay-gateway-choice">
                    {[["phonepe", "PhonePe"], ["razorpay", "Razorpay"]].map(([k, label]) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setGateway(k)}
                        className={`px-3 py-1 rounded-full text-xs border ${
                          gateway === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"
                        }`}
                        data-testid={`pay-gateway-${k}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
                <label className={"flex items-center gap-2 border border-slate-200 rounded-md p-2 " + (codBlocked ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-slate-50")}>
                  <input
                    type="radio"
                    name="pay"
                    checked={payMethod === "cod"}
                    disabled={codBlocked}
                    onChange={() => !codBlocked && setPayMethod("cod")}
                    data-testid="pay-cod"
                  />
                  <div>
                    <div className="text-sm font-medium">Cash on delivery</div>
                    <div className="mono text-[10px] text-slate-500">
                      {codBlocked
                        ? "Not available for your PIN — please pay online."
                        : "Pay when the order arrives"}
                    </div>
                  </div>
                </label>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-2">
            <Label>Notes for the seller (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything we should know?" data-testid="cart-notes" />
          </div>

          <Button
            className="w-full h-11 bg-[var(--shop-primary,#0055FF)] hover:bg-[var(--shop-primary,#0040CC)] disabled:opacity-50"
            disabled={!canPlace}
            onClick={place}
            data-testid="cart-place-order"
          >
            {placing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {list.length === 0
              ? "Add items to place order"
              : !quote?.serviced
                ? "Delivery not available"
                : payMethod === "prepaid"
                  ? `Pay ₹${total.toLocaleString("en-IN")} & place order`
                  : `Place order · ₹${total.toLocaleString("en-IN")}`}
          </Button>
          {!quote?.serviced && list.length > 0 && (
            <p className="text-xs text-red-600 text-center">
              Update your PIN in <Link to="/shop/account" className="underline">Account</Link> to a serviced area to continue.
            </p>
          )}
        </div>
      </div>
      <ShopFooter />
    </div>
  );
}
